import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
// Import useFirestore instead of useAnnouncementsHook
import useFirestore from "../../../config/fireBaseHook"; // Adjusted path

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  // Use useFirestore and specify the 'Announcements' collection
  // Optionally, you could add ordering here, e.g., useFirestore('Announcements', 'createdAt', 'desc')
  const { data, error, loading } = useFirestore("Announcements");

  // Helper function to check if an announcement is still valid
  const isAnnouncementValid = (announcement) => {
    if (!announcement.expireDate) {
      console.warn("Announcement missing expireDate:", announcement.id);
      return false;
    }

    try {
      let expireDate;

      // Check if expireDate is a Firestore Timestamp object (best case)
      if (
        announcement.expireDate &&
        typeof announcement.expireDate === "object" &&
        announcement.expireDate.seconds
      ) {
        // Direct Firestore Timestamp - most reliable
        expireDate = new Date(announcement.expireDate.seconds * 1000);
        console.log(
          "Using Firestore Timestamp for:",
          announcement.id,
          expireDate.toISOString()
        );
      } else if (typeof announcement.expireDate === "string") {
        // String format - parse it
        let parts;
        if (announcement.expireDate.includes("/")) {
          parts = announcement.expireDate.split("/");
        } else if (announcement.expireDate.includes("-")) {
          parts = announcement.expireDate.split("-");
        } else {
          console.warn(
            "Invalid expireDate format:",
            announcement.expireDate,
            "for ID:",
            announcement.id
          );
          return false;
        }

        if (parts.length !== 3) {
          console.warn(
            "Invalid expireDate parts length:",
            announcement.expireDate,
            "for ID:",
            announcement.id
          );
          return false;
        }

        // Parse the date parts
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Validate the parsed values
        if (
          isNaN(month) ||
          isNaN(day) ||
          isNaN(year) ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31
        ) {
          console.warn(
            "Invalid date values:",
            { month, day, year },
            "for ID:",
            announcement.id
          );
          return false;
        }

        // Create date using UTC to avoid timezone issues between environments
        expireDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
      } else {
        console.warn(
          "Unknown expireDate type:",
          typeof announcement.expireDate,
          announcement.expireDate,
          "for ID:",
          announcement.id
        );
        return false;
      }

      // Get current date in UTC
      const now = new Date();
      const currentDateUTC = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0,
          0,
          0,
          0
        )
      );

      const isValid = expireDate >= currentDateUTC;

      console.log("Announcement validation:", {
        id: announcement.id,
        title: announcement.title,
        expireDate: expireDate.toISOString(),
        currentDate: currentDateUTC.toISOString(),
        isValid: isValid,
      });

      return isValid;
    } catch (e) {
      console.error(
        "Error parsing expireDate:",
        announcement.expireDate,
        "for ID:",
        announcement.id,
        e
      );
      return false;
    }
  };

  useEffect(() => {
    if (data) {
      console.log("Processing announcements data:", data);
      // Filter out announcements where expireDate is in the past
      const upcomingAnnouncements = data.filter(isAnnouncementValid);
      console.log("Filtered upcoming announcements:", upcomingAnnouncements);
      setAnnouncements(upcomingAnnouncements);
    }
  }, [data]); // Dependency remains the same

  if (loading) return <div>Loading announcements...</div>; // Slightly more specific loading message
  if (error)
    return <Alert severity="error">Error loading announcements: {error}</Alert>; // Show the error message

  // Handle the case where there are no announcements to display
  if (!loading && announcements.length === 0) {
    return (
      <Box mt={2}>
        <Alert severity="info">No current announcements.</Alert>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Stack spacing={2}>
        {announcements.map((announcement) => (
          <Alert
            key={announcement.id}
            // Use severity or default to 'info' if color is missing/invalid
            severity={
              ["error", "warning", "info", "success"].includes(
                announcement.color
              )
                ? announcement.color
                : "info"
            }
            variant="filled"
            action={
              announcement.url ? (
                <Button
                  color="inherit"
                  size="small"
                  component={Link}
                  href={announcement.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "white",
                    textDecoration: "underline",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {announcement.linkText || "Learn More"}
                </Button>
              ) : null
            }
          >
            {/* Ensure content exists */}
            {announcement.content || "No content provided."}
          </Alert>
        ))}
      </Stack>
    </Box>
  );
};

export default Announcements;
